import * as nodePath from 'node:path';
import type { DirectoryNode, FileSystemTree } from '@webcontainer/api';
import { WORK_DIR } from './constants';
import type { FileMap } from '~/lib/stores/files';

interface FileTreeNode {
  [key: string]: FileTreeNode | null;
}

const textEncoder = new TextEncoder();

export function getFileTree(files: FileMap) {
  const tree: FileTreeNode = {};

  const fileEntries = Object.entries(files);

  if (!fileEntries.length) {
    return undefined;
  }

  // build tree structure from file paths
  for (const [path, entry] of Object.entries(files)) {
    if (!entry) {
      continue;
    }

    const relative = nodePath.relative(WORK_DIR, path);

    if (relative === '.') {
      continue;
    }

    const parts = relative.split(nodePath.sep).filter(Boolean);

    let current = tree;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (i === parts.length - 1) {
        current[part] = entry.type === 'file' ? null : {};
      } else {
        current[part] = current[part] || {};
        current = current[part];
      }
    }
  }

  return Object.keys(tree).length === 0 ? undefined : renderTree(tree);
}

function renderTree(node: any, prefix = ''): string {
  const entries = Object.entries(node).sort(([a], [b]) => {
    const aIsFile = node[a] === null;
    const bIsFile = node[b] === null;

    return aIsFile === bIsFile ? a.localeCompare(b) : aIsFile ? 1 : -1;
  });

  return entries
    .map(([name, children], index) => {
      const isLast = index === entries.length - 1;
      const connector = isLast ? '└── ' : '├── ';
      const line = prefix + connector + name;

      if (children && Object.keys(children).length > 0) {
        const newPrefix = prefix + (isLast ? '    ' : '│   ');
        return line + '\n' + renderTree(children, newPrefix);
      }

      return line;
    })
    .join('\n');
}

/**
 * Converts a `FileMap` to a `FileSystemTree` to mount in WebContainer.
 */
export function fileMapToTree(fileMap: FileMap): FileSystemTree {
  const tree: FileSystemTree = {};

  for (const [path, entry] of Object.entries(fileMap)) {
    if (!entry) {
      continue;
    }

    const relative = nodePath.relative(WORK_DIR, path);
    const parts = relative.split(nodePath.sep).filter(Boolean);

    let current = tree;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (i === parts.length - 1) {
        if (entry.type === 'file') {
          current[part] = {
            file: {
              contents: entry.isBinary ? textEncoder.encode(entry.content) : entry.content,
            },
          };
        } else {
          current[part] = { directory: {} };
        }
      } else {
        if (!(part in current)) {
          current[part] = { directory: {} };
        }

        current = (current[part] as DirectoryNode).directory;
      }
    }
  }

  return tree;
}
